import { useState, useRef, useCallback, useEffect } from "react";
import { TextInput } from "@mantine/core";
import urlToHttp from "@form/utils/urlToHttp";
import { FixedSizeList as List } from "react-window";
import _ from "lodash";
import useERC20Info from "@form/hooks/useERC20Info";
import useERC721Info from "@form/hooks/useERC721Info";
import { nativeCurrencys } from "@form/services/chain";

import type { FunctionComponent, KeyboardEventHandler } from "react";
import type { NFT, Token } from "@form/types";

const TokenSelector: FunctionComponent<{
  path: string;
  className?: string;
  data: (NFT | Token)[];
  chain: string;
  ariaLabel?: string;
  formik: any;
  onChange?: (token: any) => void;
  onFocus?: any;
  onBlur?: any;
  inputStyle?: any;
  dropdownClass?: any;
  hideNativeCurrency?: boolean;
}> = ({
  formik,
  path,
  data,
  chain,
  ariaLabel = "token selector",
  onChange,
  onFocus,
  onBlur,
  inputStyle,
  dropdownClass = "",
  hideNativeCurrency,
}) => {
  const [dropdownShow, setDropdownShow] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<List>(null);
  const [filter, setFilter] = useState<string | undefined>(undefined);

  const tokenList = data.filter((token) => {
    return String(token.chainId) === chain;
  });

  const nativeCurrency = (nativeCurrencys as any)[Number(chain)];

  if (nativeCurrency && !hideNativeCurrency) {
    tokenList.unshift(nativeCurrency);
  }

  const filterData = filter
    ? tokenList.filter((item) => {
        const text =
          `${item.name}_${item.symbol}_${item.address}`.toLowerCase();
        return text.indexOf(filter.toLowerCase()) > -1;
      })
    : tokenList;

  const { info } = useERC20Info(
    filterData.length === 0 ? filter : "",
    Number(chain)
  );

  const { info: nftInfo } = useERC721Info(
    filterData.length === 0 ? filter : "",
    Number(chain)
  );

  if (filterData.length === 0 && info.address) {
    filterData.unshift(info);
  }

  if (filterData.length === 0 && nftInfo.address) {
    filterData.unshift(nftInfo);
  }

  const findIndex = filterData.findIndex((item) => {
    return item.address === _.get(formik.values, path)?.address;
  });

  const [activeIndex, setActiveIndex] = useState(
    findIndex === -1 ? 0 : findIndex
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setActiveIndex(0);
  }, [chain, inputRef]);

  useEffect(() => {
    if (dropdownShow && listRef.current) {
      listRef.current.scrollToItem(activeIndex, "center");
    }
  }, [dropdownShow, activeIndex]);

  const onInputFocus = useCallback(() => {
    setDropdownShow(true);
    setFilter(undefined);
  }, []);

  const onInputBlur = useCallback(() => {
    setTimeout(() => {
      setDropdownShow(false);
    }, 200);
  }, []);

  const onUserInput: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const code = e.code;
      const totalCount = filterData.length;
      if (code === "ArrowUp" || code === "ArrowDown") {
        let index;
        if (code === "ArrowUp") {
          index = activeIndex - 1;
        } else {
          index = activeIndex + 1;
        }
        if (index < 0) {
          index = totalCount + index;
        } else if (index > totalCount - 1) {
          index = index - totalCount;
        }
        setActiveIndex(index);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (code === "Enter") {
        const token = filterData[activeIndex];
        formik.setFieldValue(path, token || undefined);
        if (token) {
          onChange && onChange(token);
        }
        if (inputRef.current) {
          inputRef.current.value = token.name;
          inputRef.current.blur();
        }
        setDropdownShow(false);
        return;
      }
      if (code === "Escape") {
        inputRef.current?.blur();
        setDropdownShow(false);
        return;
      }
      if (code !== "ArrowRight" && code !== "ArrowLeft") {
        setFilter(inputRef.current?.value || "");
      }
    },
    [inputRef, activeIndex, filterData]
  );

  const onSelect = useCallback(
    (index: any, token: any) => {
      if (inputRef.current && token) {
        inputRef.current.value = token.name;
        formik.setFieldValue(path, token || undefined);
        onChange && onChange(token || undefined);
        setActiveIndex(index);
        setDropdownShow(false);
      }
    },
    [onChange]
  );

  const Row = ({ style, index, data }: any) => {
    const item = data.list[index];

    const onSelect = useCallback(
      (e: any) => {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        data.onSelect(index, data.list[index]);
      },
      [data.list]
    );

    return (
      <div
        onClick={onSelect}
        data-index={index}
        className={`flex cursor-pointer items-center rounded px-2 text-body hover:bg-gray_e4 ${
          index == data.activeIndex ? "bg-primary text-white" : ""
        }`}
        style={style}
        role="listitem"
        aria-label={item.name}
      >
        <img
          className="mr-2 rounded-[50%]"
          src={urlToHttp(item.logoURI)[0]}
          width={20}
          height={20}
          loading="lazy"
        />
        <span>{item.name}</span>
      </div>
    );
  };

  return (
    <div className="relative">
      <TextInput
        id={path}
        onChange={(e) => {
          setFilter(e.currentTarget.value);
        }}
        rightSectionWidth={50}
        className="border-none"
        styles={{
          input: {
            border: "none",
            cursor: "pointer",
            padding: "0px",
            background: "transparent",
            ...inputStyle,
          },
        }}
        type="text"
        role="textbox"
        aria-label={ariaLabel}
        ref={inputRef}
        autoFocus={false}
        onKeyUp={onUserInput}
        onFocus={(e) => {
          onInputFocus();
          onFocus && onFocus();
        }}
        onBlur={(e) => {
          setTimeout(() => {
            onInputBlur();
            setFilter(undefined);
            formik.handleBlur(e);
            onBlur && onBlur();
          }, 200);
        }}
        autoComplete={"off"}
        spellCheck={false}
        placeholder="Search or paste address"
        value={filter === "" ? "" : filter || _.get(formik.values, path)?.name}
      ></TextInput>
      {dropdownShow && (
        <div
          className={`absolute top-[49px] z-20  w-full  rounded border  bg-white ${dropdownClass}`}
        >
          <div className="pl-2 pr-3 pt-3">
            {filterData.length === 0 ? (
              <div className="flex justify-center items-center w-full h-[246px]">
                No results found
              </div>
            ) : (
              <List
                height={246}
                itemCount={filterData.length}
                itemSize={32}
                width={"100%"}
                itemData={{
                  list: filterData,
                  activeItem: _.get(formik.values, path),
                  activeIndex,
                  onSelect,
                }}
                className="custom-scrollbar"
                ref={listRef}
              >
                {Row}
              </List>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenSelector;
